import React, { useState } from 'react';
import { Icon, Modal } from 'Components';
import useAssignmentsUserCount, {
	ResolveAssignmentUsers
} from 'LearningManager/hooks/useAssignmentsUserCount';
import LMSButton from 'LearningManager/components/Button';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import UserCountError from 'LearningManager/components/UserCountError';
import styles from './CourseResetModal.module.scss';

export interface CourseResetAssignmentOption {
	id: number;
	label: string;
}

interface CourseResetModalProps {
	visible: boolean;
	options: CourseResetAssignmentOption[];
	resolveAssignmentUsers: ResolveAssignmentUsers;
	onCancel: () => void;
	onConfirm: (assignmentIds: number[]) => void;
}

const plural = (count: number, noun: string) =>
	`${count} ${noun}${count === 1 ? '' : 's'}`;

const CourseResetModal: React.FC<CourseResetModalProps> = ({
	visible,
	options,
	resolveAssignmentUsers,
	onCancel,
	onConfirm
}) => {
	const [selectedIds, setSelectedIds] = useState<number[]>([]);

	const hasSelection = selectedIds.length > 0;
	const {
		count: userCount,
		failedCount,
		retry
	} = useAssignmentsUserCount(
		selectedIds,
		resolveAssignmentUsers,
		visible
	);

	const handleCancel = () => {
		setSelectedIds([]);
		onCancel();
	};

	const handleConfirm = () => {
		onConfirm(selectedIds);
		setSelectedIds([]);
	};

	return (
		<Modal
			visible={visible}
			title="Reset courses?"
			onCancel={handleCancel}
			size="sm"
			destroyOnClose
			className={styles.modal}
			footer={
				<div className={styles.footer}>
					<LMSButton
						size="small"
						variant="borderless"
						type="default"
						onClick={handleCancel}
					>
						Cancel
					</LMSButton>
					<LMSButton
						type="primary"
						size="small"
						disabled={!hasSelection || !userCount}
						onClick={handleConfirm}
					>
						Reset
					</LMSButton>
				</div>
			}
		>
			<div className={styles.body}>
				<p className={styles.text}>
					Select the assignment(s) you want to reset this course from.
				</p>
				<div className={styles.field}>
					<div className={styles.field__label}>Assignment</div>
					<MultiSelectDropdown
						options={options}
						placeholder="Select assignment"
						defaultSelectedIds={[]}
						onChange={setSelectedIds}
						allLabel="All Assignments"
						showSelectedLabels
						className={styles.dropdown}
					/>
				</div>

				{hasSelection && (
					<div className={styles.summary}>
						{failedCount > 0 && (
							<UserCountError
								failedCount={failedCount}
								noun="user"
								onRetry={retry}
							/>
						)}
						<p className={styles.text}>
							This action will reset the course progress for{' '}
							<strong>
								{userCount === undefined ? (
									<Icon type="loading" aria-label="Counting users" />
								) : (
									plural(userCount, 'user')
								)}
							</strong>
							.
						</p>
						<p className={styles.text}>
							All progress, completions, and scores related to this
							course will be permanently removed.
						</p>
						<p className={styles.text}>
							Users will need to start the assignment again from the
							beginning.
						</p>
						<p className={styles.text}>
							This action cannot be undone.
						</p>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default CourseResetModal;
